import type { ApiErrorBody } from './errors';

/**
 * The only place in the app that calls `fetch` — enforced by an ESLint rule. Everything about
 * talking to the backend (the `/api` prefix, cookies, the error envelope) is decided here once.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    readonly details?: unknown,
    /**
     * What was actually thrown, when this is wrapping a failure that never reached the API — see
     * `asApiError`.
     *
     * **Declared here rather than using `Error.cause`.** That is ES2022, and `@vue/tsconfig`'s DOM
     * preset pins `lib` to ES2020 to match Vite's build target, so the built-in is not in the type
     * at all. Same name and same meaning; a browser console shows it either way.
     */
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** A 401 is a normal state for a first-time visitor, not a failure to report. */
  get isNotAuthenticated(): boolean {
    return this.status === 401;
  }

  /** Someone else got there first, or the game moved on. Worth re-reading the game. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /**
   * A guest holding a perfectly good session who needs a verified email behind it.
   *
   * Deliberately distinct from `isNotAuthenticated`: this caller is signed in, so the answer is
   * "sign in with an email", not "sign in". Retrying with the same session never helps.
   */
  get isAccountRequired(): boolean {
    return this.status === 403 && this.code === 'ACCOUNT_REQUIRED';
  }

  /**
   * How long to wait after a 429. The backend puts this in the body and sends no `Retry-After`
   * header, whatever docs/API.md used to claim.
   *
   * Type-guarded rather than trusted, because `details` also carries the underlying cause for a
   * NETWORK_ERROR — it is not always the error envelope's details.
   */
  get retryAfterSeconds(): number | undefined {
    const details = this.details as { retryAfterSeconds?: unknown } | undefined;
    return typeof details?.retryAfterSeconds === 'number' ? details.retryAfterSeconds : undefined;
  }
}

/**
 * `same-origin` rather than `include`: in production the SPA and the API are served from one
 * CloudFront distribution, and locally Vite proxies /api to the backend. The session cookie is
 * always first-party, which is the whole point — see the backend's docs/AUTH.md.
 */
const CREDENTIALS: RequestCredentials = 'same-origin';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: CREDENTIALS,
      ...(body === undefined
        ? {}
        : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
    });
  } catch (cause) {
    // Offline, DNS failure, or the dev server is not running. Distinguishable from a 5xx,
    // because the user can act on it.
    throw new ApiError(0, 'NETWORK_ERROR', 'Could not reach the server', undefined, cause);
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
  const payload: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const envelope = (payload as ApiErrorBody | undefined)?.error;
    throw new ApiError(
      response.status,
      envelope?.code ?? 'UNKNOWN_ERROR',
      envelope?.message ?? `Request failed with status ${response.status}`,
      envelope?.requestId,
      envelope?.details,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
